import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS, apiRequest } from '@/lib/api';
import { DASHBOARD_NUMBERS } from '@/config/dashboardWorkflows';
import { TaskData } from './useTaskStageUpdate';

interface UpdateTaskQuarterParams {
  taskId: number;
  quarter: string;
  existingData: Record<string, unknown>;
}

export function useTaskQuarterUpdate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, quarter, existingData }: UpdateTaskQuarterParams) => {
      const url = `${API_ENDPOINTS.TASKS.LIST}${taskId}/?dashboard=${DASHBOARD_NUMBERS.product}`;
      const response = await apiRequest(url, {
        method: 'PATCH',
        body: JSON.stringify({
          dashboard: DASHBOARD_NUMBERS.product,
          data: { ...existingData, quarter },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          detail: 'Failed to update quarter',
        }));
        throw new Error(errorData.detail || 'Failed to update quarter');
      }

      return response.json();
    },
    onMutate: async ({ taskId, quarter }) => {
      await queryClient.cancelQueries({ queryKey: ['dashboard', 'product'] });
      const previousData = queryClient.getQueryData(['dashboard', 'product']);

      queryClient.setQueryData<TaskData[]>(['dashboard', 'product'], (old) => {
        if (!old) return old;
        return old.map((task) =>
          task.id === taskId
            ? {
                ...task,
                data: { ...(task.data || {}), quarter },
                dashboard_data: { ...(task.dashboard_data || task.data || {}), quarter },
              }
            : task
        );
      });

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['dashboard', 'product'], context.previousData);
      }
    },
  });
}
